/**
 * WP-ESC — Escape closes the popup.
 *
 * Misty has about 150 popup windows across 35 screens, each built on its own,
 * and only a handful ever handled the Escape key. Rather than edit 150 popups,
 * one handler is installed once for the whole program (staff screens and the
 * customer portal share the same React root, so one install covers both) and it
 * does exactly one thing: it finds the popup in front of you and presses that
 * popup's OWN close control, as if you had clicked it with the mouse.
 *
 * The rules this keeps, in Callum's words (SCOPE_Escape_Closes_Popups.md,
 * approved 18/09/2026):
 *
 *   - Nothing is saved by Escape, ever. It only ever presses a control that is
 *     already labelled Close, Cancel, X, Done or Back. A Save, Confirm, Print,
 *     Delete or Yes button is never pressed.
 *   - If a popup asks "discard your changes?" when you click its X, it asks the
 *     same thing on Escape, because it is the same button being pressed.
 *   - Popups stacked on top of each other: the top one closes, and only the top
 *     one, one popup per key press.
 *   - A popup with only Yes/No and no Cancel or X is left alone (Q2 a): Escape
 *     does nothing there, so a decision is never made for you.
 *   - Radix dialogs (components/ui/dialog.jsx, alert-dialog.jsx, sheet.jsx,
 *     drawer.jsx) close themselves on Escape. This handler stands back for them
 *     so a single press never closes two things — unless a hand-built popup has
 *     since been opened in front of the Radix dialog, when the popup the person
 *     is looking at is the one that closes.
 *   - The button pressed always belongs to the window in front. A control drawn
 *     inside a popup nested in this one is that popup's, and is left alone.
 *   - A press made while a Chinese/Japanese/Korean word is half typed belongs
 *     to the box being typed in, never to the window around it.
 *   - Every other box — a typing box, a dropdown, a date box — closes the
 *     window around it. See the note on PICKERS below for why a dropdown is
 *     not an exception.
 *
 * The census test that keeps this honest is
 * utils/__tests__/escapeClosesPopupsCensus.test.js: it walks every "fixed
 * inset-0" overlay in the source and fails if a new popup arrives with no close
 * control the handler can find, unless it is on the allowlist with a reason.
 */

// A hover text or screen-reader label only counts as "the way out of this
// window" when it BEGINS with the word Close: "Close", "Close this window",
// "Close without saving". Anywhere-in-the-sentence matching is not safe — the
// invoicing screen has a Mark-as-paid button whose hover text ends "...once it
// is closed", and Escape pressing that would take money off a statement.
const CLOSE_LABEL = /^\s*close\b/i;
// Cancel is accepted only as a label on its own, never as "Cancel this order"
// or "Cancel the delivery", which do something rather than shut a window.
const CANCEL_LABEL = /^\s*cancel\s*$/i;

// The visible words a button may carry to count as this popup's close control.
// Compared against the button's whole trimmed text, lower-cased, so a "Cancel
// order" button is NOT treated as the way out of the window.
export const CLOSE_TEXTS = ['×', '✕', '✖', 'x', 'close', 'cancel', 'done', 'back'];

// What can be pressed. Anything else on a popup is text, not a control.
const CLICKABLE = 'button, [role="button"], a[href], input[type="button"], input[type="submit"]';

// Misty draws a popup in one of two ways, and both are full-screen fixed
// layers: the Tailwind pair "fixed inset-0" written on the element (about 140
// popups), and the shared `.modal-overlay` class in App.css, which is the same
// thing behind a name (about 65 more, on payroll, orders, staff and the rest).
// Both have to be recognised or Escape works on half the program.
const OVERLAY = '.fixed.inset-0, .modal-overlay';

// A Radix layer always stamps its own open/closed state on the element. Radix
// owns the Escape key for those, so they are skipped here.
const RADIX_OPEN = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

// The same two overlay shapes, but only while Radix has stamped them open —
// the backdrop half of a Radix dialog, which is usually the half carrying the
// z-index.
const RADIX_BACKDROP = '.fixed.inset-0[data-state="open"], .modal-overlay[data-state="open"]';

function isVisible(el) {
  if (!el || !el.isConnected) return false;
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const view = el.ownerDocument && el.ownerDocument.defaultView;
  if (!view || typeof view.getComputedStyle !== 'function') return true;
  const style = view.getComputedStyle(el);
  if (!style) return true;
  if (style.display === 'none') return false;
  if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  if (style.opacity !== '' && Number(style.opacity) === 0) return false;
  return true;
}

// How high up the stack this popup sits. A popup with no z-index of its own
// counts as 0, which is what the browser does too.
function stackLevel(el) {
  const view = el.ownerDocument && el.ownerDocument.defaultView;
  if (!view || typeof view.getComputedStyle !== 'function') return 0;
  const raw = view.getComputedStyle(el).zIndex;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

function isRadixLayer(el) {
  // Radix's own overlay stamps its open/closed state on the overlay element
  // itself. Only the element is checked, never its ancestors: a hand-built
  // Misty popup can easily sit inside a Radix tab panel, which carries a
  // data-state of its own and has nothing to do with Escape.
  return el.hasAttribute('data-state');
}

// The Radix layer in front, as a level and a node, or null when Radix has
// nothing open. A Radix dialog is drawn as two elements — a backdrop and the
// panel — and only the backdrop usually carries the z-index, so the pair is
// measured together and the higher of the two is the level Radix sits at.
function topmostRadixLayer(doc) {
  const parts = [];
  const panels = doc.querySelectorAll(RADIX_OPEN);
  for (let i = 0; i < panels.length; i += 1) {
    if (isVisible(panels[i])) parts.push(panels[i]);
  }
  if (!parts.length) return null;
  const backdrops = doc.querySelectorAll(RADIX_BACKDROP);
  for (let i = 0; i < backdrops.length; i += 1) {
    if (isVisible(backdrops[i])) parts.push(backdrops[i]);
  }
  let best = null;
  let bestLevel = 0;
  for (let i = 0; i < parts.length; i += 1) {
    const level = stackLevel(parts[i]);
    if (best === null || level >= bestLevel) {
      best = parts[i];
      bestLevel = level;
    }
  }
  return { node: best, level: bestLevel };
}

// Is the hand-built popup drawn in front of that Radix layer? Only a popup
// higher up the stack counts. Level for level, the answer is no on purpose: at
// the same height either one could be the window in front, and standing back
// leaves one key press closing one thing rather than two.
function isInFrontOf(overlay, radix) {
  if (!overlay || !radix || !radix.node) return false;
  return stackLevel(overlay) > radix.level;
}

/**
 * The popup in front of the person: the visible overlay highest up the stack,
 * and of those the last one the screen drew.
 */
export function topmostOverlay(doc) {
  const all = doc.querySelectorAll(OVERLAY);
  let best = null;
  let bestLevel = 0;
  for (let i = 0; i < all.length; i += 1) {
    const el = all[i];
    if (isRadixLayer(el)) continue;
    if (!isVisible(el)) continue;
    const level = stackLevel(el);
    // ">=" so that when a backdrop and its panel share a level, the panel — the
    // one drawn last, the one carrying the buttons — wins.
    if (best === null || level >= bestLevel) {
      best = el;
      bestLevel = level;
    }
  }
  return best;
}

function pressable(el) {
  if (el.disabled) return false;
  if (el.getAttribute('aria-disabled') === 'true') return false;
  return isVisible(el);
}

// A control drawn inside a popup that sits inside this one belongs to that
// popup, not to this one. Escape must never reach past the window in front of
// the person and press a button on a window behind or inside it.
function insideAnotherPopup(el, overlay) {
  let parent = el.parentElement;
  while (parent && parent !== overlay) {
    if (typeof parent.matches === 'function' && parent.matches(OVERLAY)) return true;
    parent = parent.parentElement;
  }
  return false;
}

function controlsIn(overlay) {
  const list = [];
  if (typeof overlay.matches === 'function' && overlay.matches(CLICKABLE)) list.push(overlay);
  const found = overlay.querySelectorAll(CLICKABLE);
  for (let i = 0; i < found.length; i += 1) {
    if (insideAnotherPopup(found[i], overlay)) continue;
    list.push(found[i]);
  }
  return list.filter(pressable);
}

function wordsOf(el) {
  return (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * This popup's own close control, looked for in the documented order:
 * an aria-label saying close, then one saying cancel, then a title saying
 * close, then a button whose visible word is one of the close words.
 * Returns null when the popup has none — a Yes/No prompt, say — and null is
 * the answer that leaves the popup alone.
 */
export function findCloseControl(overlay) {
  const controls = controlsIn(overlay);
  for (let i = 0; i < controls.length; i += 1) {
    if (CLOSE_LABEL.test(controls[i].getAttribute('aria-label') || '')) return controls[i];
  }
  for (let i = 0; i < controls.length; i += 1) {
    if (CANCEL_LABEL.test(controls[i].getAttribute('aria-label') || '')) return controls[i];
  }
  for (let i = 0; i < controls.length; i += 1) {
    if (CLOSE_LABEL.test(controls[i].getAttribute('title') || '')) return controls[i];
  }
  for (let i = 0; i < controls.length; i += 1) {
    // A control that carries its own screen-reader label has already been
    // described by whoever built it. If that description is not about closing,
    // the word printed on the button does not overrule it — the two-factor
    // prompt's "Back" says "go back to the Set up now and Not now choice", and
    // that is not a way out of the window.
    const label = controls[i].getAttribute('aria-label');
    if (label && !CLOSE_LABEL.test(label) && !CANCEL_LABEL.test(label)) continue;
    if (CLOSE_TEXTS.indexOf(wordsOf(controls[i])) !== -1) return controls[i];
  }
  return null;
}

// PICKERS — why a dropdown, a date box or a suggestion box is NOT an exception.
//
// The first review round made this handler stand back from every press made
// while the cursor sat in a <select>, a date box or a box with a suggestion
// list, on the reasoning that the list or the calendar would want the press.
// That was wrong twice over, and it left 195 dropdowns and 52 date boxes able
// to swallow the key:
//
//   - A dropdown or a calendar is only ever open for the moment somebody is
//     choosing. The cursor stays in the box for as long as they leave it there
//     afterwards, which is the ordinary state of a form once a dropdown has
//     been used — and in that state the press had nowhere to go at all. The
//     Manage-surcharge window could not be closed from the keyboard once the
//     cursor was in "Amount type" or "Effective from".
//   - While a native list or calendar IS open the browser deals with the press
//     itself, and hands on nothing this handler would act on, or hands it on
//     already marked as handled — which the defaultPrevented check above
//     catches, the same as every other control that deals with its own Escape.
//
// So the box the cursor is in does not change what Escape does. The only press
// that still belongs to the box is a half-typed Chinese, Japanese or Korean
// word, which the browser reports plainly (isComposing / key code 229) and
// which is checked in handleEscapeKey.

/**
 * The whole behaviour, exported so the tests can drive it without a window.
 */
export function handleEscapeKey(event) {
  if (!event) return null;
  if (event.key !== 'Escape' && event.key !== 'Esc') return null;
  // Holding the key down must not walk back through a stack of popups.
  if (event.repeat) return null;
  // Something nearer the keyboard has already dealt with this press — an input
  // cancelling its own edit, a library closing its own menu.
  if (event.defaultPrevented) return null;

  // A half-typed Chinese, Japanese or Korean word is cancelled with Escape, and
  // the browser reports that press as a composition. It belongs to the box
  // being typed in, never to the window around it.
  if (event.isComposing || event.keyCode === 229) return null;

  const target = event.target;
  const doc = (target && target.ownerDocument) || (typeof document !== 'undefined' ? document : null);
  if (!doc) return null;

  const overlay = topmostOverlay(doc);

  // Radix closes its own dialogs on Escape. Standing back is what stops a
  // single press closing the dialog and then the popup behind it — unless the
  // person has since opened a hand-built popup in FRONT of the Radix dialog, in
  // which case the popup they are looking at is the one that should close.
  const radix = topmostRadixLayer(doc);
  if (radix && !isInFrontOf(overlay, radix)) return null;

  if (!overlay) return null;

  const control = findCloseControl(overlay);
  if (!control) return null; // Yes/No prompt — left exactly as it was.

  if (typeof event.preventDefault === 'function') event.preventDefault();
  control.click();
  return control;
}

let installed = null;

/**
 * Wire Escape up once for the whole program. Safe to call again: the second
 * call adds nothing and hands back the same way to unwire it.
 */
export function installEscapeHandler(target) {
  const host = target || (typeof window !== 'undefined' ? window : null);
  if (!host || typeof host.addEventListener !== 'function') return () => {};
  if (installed && installed.host === host) return installed.remove;

  const handler = (event) => {
    try {
      handleEscapeKey(event);
    } catch (e) {
      // A popup that cannot be read is not a reason to swallow somebody's
      // typing. Escape simply does nothing on that one.
    }
  };

  // The window, in the bubble phase, deliberately last: React's own onKeyDown
  // handlers and Radix (which listens on the document, capturing) both run
  // first, so anything that wants to handle its own Escape still gets to.
  host.addEventListener('keydown', handler);

  const remove = () => {
    if (installed && installed.handler === handler) {
      host.removeEventListener('keydown', handler);
      installed = null;
    }
  };
  installed = { host, handler, remove };
  return remove;
}

export function escapeHandlerIsInstalled() {
  return installed !== null;
}
